import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MagasinsEnCompteView.css';
import { NewSideBarMagasins } from '../../NewSidebarMagasin';
import AfficheMagasinsService from '../../../Services/Magasin/AfficheMagasinsService';

export interface MagasinEnCompteClientEntry {
  id: number;
  magasin_fournisseur: number;
  magasin_client: number;
  NomMagasinClient?: string;
  date: string;
  solde: number | string;
  created_at?: string;
  updated_at?: string;
}

export interface MagasinEnCompteFournisseurEntry {
  id: number;
  magasin_fournisseur: number;
  magasin_client: number;
  NomMagasinFournisseur?: string;
  date: string;
  solde: number | string;
  created_at?: string;
  updated_at?: string;
}

export const MagasinsEnCompteView: React.FC = () => {
  const MagasinId = localStorage.getItem('MagasinId');
  const MagasinName = localStorage.getItem('MagasinName');

  const [activeTab, setActiveTab] = useState<'clients' | 'fournisseurs'>('clients');
  const [clients, setClients] = useState<MagasinEnCompteClientEntry[]>([]);
  const [fournisseurs, setFournisseurs] = useState<MagasinEnCompteFournisseurEntry[]>([]);
  const [allMagasins, setAllMagasins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Currently active en-compte supplier in session
  const [activeSupplierId, setActiveSupplierId] = useState<string | null>(
    localStorage.getItem('en_compte_active') === '1' ? localStorage.getItem('en_compte_supplier_id') : null
  );

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<MagasinEnCompteClientEntry | null>(null);

  // Form states
  const [formMagasinClient, setFormMagasinClient] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formSolde, setFormSolde] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch entries
  const fetchEntries = async () => {
    if (!MagasinId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/en-compte`
      );

      let fetchedClients: MagasinEnCompteClientEntry[] = [];
      let fetchedFournisseurs: MagasinEnCompteFournisseurEntry[] = [];

      if (Array.isArray(res.data)) {
        fetchedClients = res.data;
      } else if (res.data) {
        fetchedClients = res.data.clients || [];
        fetchedFournisseurs = res.data.fournisseurs || [];
      }

      setClients(fetchedClients);
      setFournisseurs(fetchedFournisseurs);

      // If user has suppliers but no clients, default to fournisseurs tab
      if (fetchedClients.length === 0 && fetchedFournisseurs.length > 0) {
        setActiveTab('fournisseurs');
      }
    } catch (err) {
      console.error('Error fetching en-compte entries:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all magasins for dropdown
  const fetchAllMagasins = async () => {
    try {
      const res = await AfficheMagasinsService().getMagasin();
      const list = res.data || [];
      // Filter out current magasin
      setAllMagasins(list.filter((m: any) => String(m.IdMagasin || m.id) !== String(MagasinId)));
    } catch (err) {
      console.error('Error fetching magasins:', err);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchAllMagasins();
  }, [MagasinId]);

  // Activate / deactivate supplier en-compte mode
  const handleToggleSupplierMode = (supplier: MagasinEnCompteFournisseurEntry) => {
    const isCurrentlyActive = activeSupplierId === String(supplier.magasin_fournisseur);

    if (isCurrentlyActive) {
      localStorage.removeItem('en_compte_active');
      localStorage.removeItem('en_compte_supplier_id');
      localStorage.removeItem('en_compte_supplier_name');
      localStorage.removeItem('en_compte_solde');
      setActiveSupplierId(null);
    } else {
      localStorage.setItem('en_compte_active', '1');
      localStorage.setItem('en_compte_supplier_id', String(supplier.magasin_fournisseur));
      localStorage.setItem('en_compte_supplier_name', supplier.NomMagasinFournisseur || `Magasin #${supplier.magasin_fournisseur}`);
      localStorage.setItem('en_compte_solde', String(supplier.solde || '0'));
      setActiveSupplierId(String(supplier.magasin_fournisseur));
    }
  };

  // Handle Add Client
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formMagasinClient) {
      setErrorMessage('Veuillez sélectionner un magasin client.');
      return;
    }
    setSubmitting(true);
    setErrorMessage('');
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/en-compte/add`,
        {
          magasin_client: formMagasinClient,
          date: formDate,
          solde: parseFloat(formSolde) || 0,
        }
      );
      setShowAddModal(false);
      setFormMagasinClient('');
      setFormSolde('0');
      fetchEntries();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || "Erreur lors de l'ajout.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Update Solde
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntry) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/en-compte/${selectedEntry.id}/update-solde`,
        {
          solde: parseFloat(formSolde) || 0,
        }
      );
      setShowEditModal(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete
  const handleDelete = async (entryId: number) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette relation en compte ?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_PHP_APP_URL}/magasins/${MagasinId}/en-compte/${entryId}`
      );
      fetchEntries();
    } catch (err) {
      console.error('Error deleting en-compte entry:', err);
    }
  };

  // Filtered lists
  const filteredClients = clients.filter((item) =>
    (item.NomMagasinClient || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredFournisseurs = fournisseurs.filter((item) =>
    (item.NomMagasinFournisseur || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Stats calculations
  const totalSoldeClients = clients.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.solde)) || 0),
    0
  );

  const totalSoldeFournisseurs = fournisseurs.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.solde)) || 0),
    0
  );

  return (
    <div className="mec-wrapper">
      <NewSideBarMagasins />
      <div className="mec-main">
        {/* Header */}
        <div className="mec-header">
          <div className="mec-title-group">
            <h1>
              <i className="bi bi-wallet2" style={{ color: '#d97706' }} />
              Magasins En Compte
            </h1>
            <p>
              Gérez vos relations en compte pour <strong>{MagasinName}</strong> (crédits accordés et fournisseurs liés).
            </p>
          </div>
          <div className="mec-actions-group">
            {activeTab === 'clients' && (
              <button className="mec-btn-add" onClick={() => setShowAddModal(true)}>
                <i className="bi bi-plus-lg" />
                Ajouter un Magasin Client
              </button>
            )}
          </div>
        </div>

        {/* Tab Selection */}
        <div className="mec-tabs-container">
          <button
            className={`mec-tab-btn ${activeTab === 'fournisseurs' ? 'active' : ''}`}
            onClick={() => setActiveTab('fournisseurs')}
          >
            <i className="bi bi-building-check" />
            Mes Fournisseurs En Compte
            <span className="mec-tab-count">{fournisseurs.length}</span>
          </button>
          <button
            className={`mec-tab-btn ${activeTab === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveTab('clients')}
          >
            <i className="bi bi-people-fill" />
            Mes Clients En Compte
            <span className="mec-tab-count">{clients.length}</span>
          </button>
        </div>

        {/* Stats Grid */}
        <div className="mec-stats-grid">
          {activeTab === 'fournisseurs' ? (
            <>
              <div className="mec-stat-card">
                <div className="mec-stat-icon amber">
                  <i className="bi bi-building-check" />
                </div>
                <div className="mec-stat-content">
                  <h4>Fournisseurs Liés</h4>
                  <p>{fournisseurs.length}</p>
                </div>
              </div>

              <div className="mec-stat-card">
                <div className="mec-stat-icon emerald">
                  <i className="bi bi-cash-stack" />
                </div>
                <div className="mec-stat-content">
                  <h4>Total Crédit Accordé</h4>
                  <p>
                    {totalSoldeFournisseurs.toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    MAD
                  </p>
                </div>
              </div>

              <div className="mec-stat-card">
                <div className="mec-stat-icon blue">
                  <i className="bi bi-toggle-on" />
                </div>
                <div className="mec-stat-content">
                  <h4>Mode Actif</h4>
                  <p style={{ fontSize: 16, fontWeight: 700, color: activeSupplierId ? '#059669' : '#64748b' }}>
                    {activeSupplierId
                      ? fournisseurs.find((f) => String(f.magasin_fournisseur) === activeSupplierId)?.NomMagasinFournisseur || 'Activé'
                      : 'Aucun (Standard)'}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mec-stat-card">
                <div className="mec-stat-icon blue">
                  <i className="bi bi-shop" />
                </div>
                <div className="mec-stat-content">
                  <h4>Clients En Compte</h4>
                  <p>{clients.length}</p>
                </div>
              </div>

              <div className="mec-stat-card">
                <div className="mec-stat-icon emerald">
                  <i className="bi bi-cash-stack" />
                </div>
                <div className="mec-stat-content">
                  <h4>Total Solde Dû</h4>
                  <p>
                    {totalSoldeClients.toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    MAD
                  </p>
                </div>
              </div>

              <div className="mec-stat-card">
                <div className="mec-stat-icon amber">
                  <i className="bi bi-calculator" />
                </div>
                <div className="mec-stat-content">
                  <h4>Solde Moyen</h4>
                  <p>
                    {(clients.length > 0 ? totalSoldeClients / clients.length : 0).toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    MAD
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Search Toolbar */}
        <div className="mec-toolbar">
          <div className="mec-search-box">
            <i className="bi bi-search" style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder={
                activeTab === 'fournisseurs'
                  ? 'Rechercher par nom de fournisseur...'
                  : 'Rechercher par nom de magasin client...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
            {activeTab === 'fournisseurs'
              ? `${filteredFournisseurs.length} fournisseur(s) trouvé(s)`
              : `${filteredClients.length} client(s) trouvé(s)`}
          </span>
        </div>

        {/* Table Card (desktop) */}
        <div className="mec-table-card">
          {loading ? (
            <div className="mec-empty">
              <i className="bi bi-hourglass-split" />
              <p>Chargement des magasins en compte...</p>
            </div>
          ) : activeTab === 'fournisseurs' ? (
            /* Fournisseurs */
            filteredFournisseurs.length === 0 ? (
              <div className="mec-empty">
                <i className="bi bi-inbox" />
                <p>Aucun fournisseur ne vous a ajouté en compte pour le moment.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <table className="mec-table">
                  <thead>
                    <tr>
                      <th>Magasin Fournisseur</th>
                      <th>Date d'accord</th>
                      <th>Solde Accordé</th>
                      <th style={{ textAlign: 'right' }}>Statut & Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFournisseurs.map((supplier) => {
                      const isActive = activeSupplierId === String(supplier.magasin_fournisseur);
                      return (
                        <tr key={supplier.id}>
                          <td>
                            <div className="mec-client-cell">
                              <div className="mec-client-avatar supplier">
                                <i className="bi bi-building" />
                              </div>
                              <div>
                                <span className="mec-client-name">
                                  {supplier.NomMagasinFournisseur || `Magasin #${supplier.magasin_fournisseur}`}
                                </span>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                  Fournisseur / Créditeur
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <i className="bi bi-calendar-event" style={{ marginRight: 6, color: '#94a3b8' }} />
                            {supplier.date ? new Date(supplier.date).toLocaleDateString('fr-FR') : 'N/A'}
                          </td>
                          <td>
                            <span className="mec-solde-tag">
                              <i className="bi bi-coin" />
                              {parseFloat(String(supplier.solde)).toLocaleString('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              MAD
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className={`mec-btn-toggle-mode ${isActive ? 'active' : ''}`}
                              onClick={() => handleToggleSupplierMode(supplier)}
                            >
                              <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-power'}`} />
                              {isActive ? 'Mode Actif' : 'Activer'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Mobile Card List */}
                <div className="mec-mobile-list">
                  {filteredFournisseurs.map((supplier) => {
                    const isActive = activeSupplierId === String(supplier.magasin_fournisseur);
                    return (
                      <div key={supplier.id} className={`mec-mobile-card supplier-card${isActive ? ' active-card' : ''}`}>
                        <div className="mec-mc-header">
                          <div className="mec-mc-identity">
                            <div className="mec-mc-avatar">
                              <i className="bi bi-building" />
                            </div>
                            <div>
                              <div className="mec-mc-name">
                                {supplier.NomMagasinFournisseur || `Magasin #${supplier.magasin_fournisseur}`}
                              </div>
                              <div className="mec-mc-role">Fournisseur / Créditeur</div>
                            </div>
                          </div>
                        </div>
                        <div className="mec-mc-body">
                          <div className="mec-mc-date">
                            <i className="bi bi-calendar-event" style={{ color: '#94a3b8' }} />
                            {supplier.date ? new Date(supplier.date).toLocaleDateString('fr-FR') : 'N/A'}
                          </div>
                          <div className="mec-mc-solde">
                            {parseFloat(String(supplier.solde)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                          </div>
                        </div>
                        <div className="mec-mc-footer">
                          <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Mode en-compte</span>
                          <button
                            className={`mec-mc-toggle${isActive ? ' active' : ''}`}
                            onClick={() => handleToggleSupplierMode(supplier)}
                          >
                            <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-power'}`} />
                            {isActive ? 'Actif ✓' : 'Activer'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            /* Clients */
            filteredClients.length === 0 ? (
              <div className="mec-empty">
                <i className="bi bi-inbox" />
                <p>Aucun magasin client en compte pour le moment.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <table className="mec-table">
                  <thead>
                    <tr>
                      <th>Magasin Client</th>
                      <th>Date d'accord</th>
                      <th>Solde Actuel</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <div className="mec-client-cell">
                            <div className="mec-client-avatar">
                              <i className="bi bi-shop" />
                            </div>
                            <div>
                              <span className="mec-client-name">
                                {entry.NomMagasinClient || `Magasin #${entry.magasin_client}`}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <i className="bi bi-calendar-event" style={{ marginRight: 6, color: '#94a3b8' }} />
                          {entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : 'N/A'}
                        </td>
                        <td>
                          <span className="mec-solde-tag">
                            <i className="bi bi-coin" />
                            {parseFloat(String(entry.solde)).toLocaleString('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{' '}
                            MAD
                          </span>
                        </td>
                        <td>
                          <div className="mec-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              className="mec-btn-action edit"
                              title="Modifier le solde"
                              onClick={() => {
                                setSelectedEntry(entry);
                                setFormSolde(String(entry.solde));
                                setShowEditModal(true);
                              }}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              className="mec-btn-action delete"
                              title="Supprimer la relation"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Card List */}
                <div className="mec-mobile-list">
                  {filteredClients.map((entry) => (
                    <div key={entry.id} className="mec-mobile-card">
                      <div className="mec-mc-header">
                        <div className="mec-mc-identity">
                          <div className="mec-mc-avatar client-av">
                            <i className="bi bi-shop" />
                          </div>
                          <div>
                            <div className="mec-mc-name">
                              {entry.NomMagasinClient || `Magasin #${entry.magasin_client}`}
                            </div>
                            <div className="mec-mc-role">Client en compte</div>
                          </div>
                        </div>
                      </div>
                      <div className="mec-mc-body">
                        <div className="mec-mc-date">
                          <i className="bi bi-calendar-event" style={{ color: '#94a3b8' }} />
                          {entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : 'N/A'}
                        </div>
                        <div className="mec-mc-solde">
                          {parseFloat(String(entry.solde)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                        </div>
                      </div>
                      <div className="mec-mc-footer">
                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Actions</span>
                        <div className="mec-mc-actions">
                          <button
                            className="mec-mc-btn edit"
                            onClick={() => {
                              setSelectedEntry(entry);
                              setFormSolde(String(entry.solde));
                              setShowEditModal(true);
                            }}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            className="mec-mc-btn delete"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>

      {/* Modal Add Client */}
      {showAddModal && (
        <div className="mec-modal-overlay">
          <div className="mec-modal">
            <div className="mec-modal-header">
              <h3>Ajouter un Magasin Client En Compte</h3>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
                onClick={() => setShowAddModal(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="mec-modal-body">
                {errorMessage && (
                  <div
                    style={{
                      background: '#fee2e2',
                      color: '#dc2626',
                      padding: '10px 14px',
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    {errorMessage}
                  </div>
                )}
                <div className="mec-form-group">
                  <label>Sélectionner le Magasin Client</label>
                  <select
                    value={formMagasinClient}
                    onChange={(e) => setFormMagasinClient(e.target.value)}
                    required
                  >
                    <option value="">-- Choisir un magasin --</option>
                    {allMagasins.map((m: any) => (
                      <option key={m.IdMagasin || m.id} value={m.IdMagasin || m.id}>
                        {m.NomMagasin || m.Nom_magasin || `Magasin #${m.IdMagasin || m.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mec-form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    required
                  />
                </div>

                <div className="mec-form-group">
                  <label>Solde Initial (MAD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formSolde}
                    onChange={(e) => setFormSolde(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="mec-modal-footer">
                <button
                  type="button"
                  className="mec-btn-cancel"
                  onClick={() => setShowAddModal(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="mec-btn-submit" disabled={submitting}>
                  {submitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Edit Solde */}
      {showEditModal && selectedEntry && (
        <div className="mec-modal-overlay">
          <div className="mec-modal">
            <div className="mec-modal-header">
              <h3>Modifier le Solde</h3>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
                onClick={() => setShowEditModal(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="mec-modal-body">
                {errorMessage && (
                  <div
                    style={{
                      background: '#fee2e2',
                      color: '#dc2626',
                      padding: '10px 14px',
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    {errorMessage}
                  </div>
                )}
                <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>
                  Magasin Client :{' '}
                  <strong>
                    {selectedEntry.NomMagasinClient || `Magasin #${selectedEntry.magasin_client}`}
                  </strong>
                </p>

                <div className="mec-form-group">
                  <label>Nouveau Solde (MAD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formSolde}
                    onChange={(e) => setFormSolde(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="mec-modal-footer">
                <button
                  type="button"
                  className="mec-btn-cancel"
                  onClick={() => setShowEditModal(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="mec-btn-submit" disabled={submitting}>
                  {submitting ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagasinsEnCompteView;

